// src/components/practice/PracticeFlashcards.jsx
//
// GENERIC flashcards play view -- extracted VERBATIM from the set-text reader's
// FlashcardsView (D4-A) so both the set-text reader and the skills reader mount the
// same component. Behaviour is byte-identical to the original: flip front<->back,
// shuffle the full deck, progress + prev/next. In-memory only (no persistence).
// `deck` is the gated set of flashcard items ({content:{front,back}}).
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { clampCardIndex } from './practiceHelpers.js';
import '../reader/lesson.css';

// Horizontal travel (px) that counts as a swipe rather than a tap. Below this the
// gesture flips the card; above it, and only when the movement is more horizontal
// than vertical, it changes card -- so a normal page scroll never turns into a swipe.
const SWIPE_PX = 45;

export default function PracticeFlashcards({ deck, showCta, title }) {
    const baseCards = useMemo(
        () => (deck || []).map((it) => ({
            id: it.id,
            front: (it.content && it.content.front) || '',
            back: (it.content && it.content.back) || '',
        })),
        [deck],
    );

    const [order, setOrder] = useState(null); // null = natural order; array = shuffled indices
    const [idx, setIdx] = useState(0);
    const [flipped, setFlipped] = useState(false);

    // Reset when the deck size changes (e.g. paid cards arrive after entitlement resolves).
    useEffect(() => { setOrder(null); setIdx(0); setFlipped(false); }, [baseCards.length]);

    const cards = useMemo(() => {
        if (!order) return baseCards;
        return order.map((i) => baseCards[i]).filter(Boolean);
    }, [baseCards, order]);

    const shuffled = !!order;
    const shuffleCards = () => {
        if (shuffled) { setOrder(null); }
        else {
            // Fisher-Yates over the FULL deck (preserves every card, just reorders).
            const o = baseCards.map((_, i) => i);
            for (let i = o.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [o[i], o[j]] = [o[j], o[i]];
            }
            setOrder(o);
        }
        setIdx(0);
        setFlipped(false);
    };

    if (cards.length === 0) {
        // F5: empty state, no stray CTA.
        return (
            <div className="max-w-3xl mx-auto text-center py-20">
                <div className="flex justify-center mb-4 text-book-accent/40"><IconCards large /></div>
                <p className="font-serif italic text-book-muted text-lg">No flashcards available for this pack yet.</p>
            </div>
        );
    }

    // The deck can SHRINK under a mounted component (entitlement revoked
    // mid-session -> paid cards drop out). The reset effect above runs only
    // AFTER this render, so for one render a stale idx can point past the new
    // (non-empty) deck -- clamp every read so cards[safeIdx] is never undefined.
    const safeIdx = clampCardIndex(idx, cards.length);
    const card = cards[safeIdx];

    const goPrev = () => { setFlipped(false); setIdx((i) => Math.max(0, clampCardIndex(i, cards.length) - 1)); };
    const goNext = () => { setFlipped(false); setIdx((i) => Math.min(cards.length - 1, clampCardIndex(i, cards.length) + 1)); };

    return (
        <div className="max-w-3xl mx-auto pb-8">
            <div className="mb-3 text-center">
                <div className="font-display text-book-accent text-xs mb-2 font-semibold">Flash Cards</div>
                <h2 className="font-display text-2xl md:text-3xl text-book-text mb-1">{title}</h2>
                <p className="font-serif italic text-book-muted text-sm">
                    Card {safeIdx + 1} of {cards.length} · Tap to flip, swipe to move {shuffled && <span className="text-book-accent">· Shuffled</span>}
                </p>
            </div>

            {/* 3b: progress DOTS rather than a bar -- a deck is a countable number of
                cards, and the dots say how many are left at a glance. Capped so a very
                large deck does not turn into a grey stripe. */}
            <ProgressDots count={cards.length} index={safeIdx} />

            {/* Card. Tap flips; a horizontal swipe moves. */}
            <SwipeCard onSwipeLeft={goNext} onSwipeRight={goPrev} onTap={() => setFlipped((f) => !f)}>
                <div className={`flip-card ${flipped ? 'flipped' : ''} w-full mx-auto cursor-pointer`} style={{ minHeight: '180px', aspectRatio: '16/7' }}>
                    <div className="flip-card-inner">
                        <div className="btl-flashcard btl-flashcard-front flip-card-front flex items-center justify-center p-5">
                            <div className="text-center">
                                <div className="btl-flashcard-label font-display text-xs mb-3 font-semibold text-book-muted">Question</div>
                                <p className="btl-flashcard-text font-serif leading-relaxed text-lg md:text-xl">{card.front}</p>
                                <div className="btl-flashcard-hint mt-4 text-xs font-serif italic">Tap to reveal</div>
                            </div>
                        </div>
                        <div className="btl-flashcard btl-flashcard-back flip-card-back flex items-center justify-center p-5">
                            <div className="text-center">
                                <div className="btl-flashcard-label font-display text-xs mb-3 font-semibold text-book-muted">Answer</div>
                                <p className="btl-flashcard-text font-serif leading-relaxed text-lg md:text-xl">{card.back}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </SwipeCard>

            {/* Nav. The buttons STAY: swipe is an addition, not a replacement -- they are
                the keyboard/desktop path and the accessible one. */}
            <div className="flex items-center justify-between mt-4 gap-2">
                <button onClick={goPrev} disabled={safeIdx === 0} aria-label="Previous card"
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-book-line text-book-accent font-display text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-book-accent/5 transition-colors font-semibold">
                    <IconChevronLeft /> Prev
                </button>
                <button onClick={shuffleCards}
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-book-line text-book-muted hover:text-book-accent font-display text-xs transition-colors font-semibold">
                    <IconRefresh /> {shuffled ? 'Reset order' : 'Shuffle'}
                </button>
                <button onClick={goNext} disabled={safeIdx === cards.length - 1} aria-label="Next card"
                    className="flex items-center gap-2 px-4 py-2 rounded-full border border-book-line text-book-accent font-display text-xs disabled:opacity-30 disabled:cursor-not-allowed hover:bg-book-accent/5 transition-colors font-semibold">
                    Next <IconChevronRight />
                </button>
            </div>

            {showCta && (
                <div className="mt-6 max-w-md mx-auto border border-book-accent/20 bg-book-bg/40 p-3 text-center">
                    <div className="font-display text-book-muted/80 text-xs mb-1 font-semibold">🔒 More with purchase</div>
                    <p className="font-serif italic text-book-muted/70 text-xs leading-snug font-semibold">
                        More flash cards unlock with the pack.
                    </p>
                </div>
            )}
        </div>
    );
}

// Deck position as dots. Above ~24 cards the dots would be unreadable, so it falls
// back to a bar -- the same information, at a size the eye can still parse.
function ProgressDots({ count, index }) {
    if (count > 24) {
        const pct = ((index + 1) / count) * 100;
        return (
            <div className="h-1 bg-book-accent/10 rounded-full mb-4 overflow-hidden">
                <div className="h-full bg-book-accent transition-all duration-500" style={{ width: pct + '%' }} />
            </div>
        );
    }
    return (
        <div className="btl-card-dots mb-4" aria-hidden="true">
            {Array.from({ length: count }, (_, i) => (
                <span key={i} className={'btl-card-dot' + (i === index ? ' btl-card-dot--now' : (i < index ? ' btl-card-dot--seen' : ''))} />
            ))}
        </div>
    );
}

// Touch swipe wrapper. Deliberately plain listeners rather than a gesture library:
//   - a horizontal move past SWIPE_PX, and more horizontal than vertical, changes card;
//   - anything shorter is a tap, which flips;
//   - a mostly-vertical drag is left alone entirely, so page scrolling still works.
// Mouse users are unaffected (click -> onTap via the click handler).
function SwipeCard({ onSwipeLeft, onSwipeRight, onTap, children }) {
    const start = useRef(null);
    // A touchend that moved far enough is a swipe -- but the browser STILL synthesises
    // a click afterwards, which would flip the card we just moved to. This flag makes
    // the click that follows a swipe a no-op.
    const swiped = useRef(false);

    const onTouchStart = (e) => {
        const t = e.touches && e.touches[0];
        start.current = t ? { x: t.clientX, y: t.clientY } : null;
        swiped.current = false;
    };
    const onTouchEnd = (e) => {
        const s = start.current;
        start.current = null;
        const t = e.changedTouches && e.changedTouches[0];
        if (!s || !t) return;
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;
        // Horizontal, and clearly more horizontal than vertical -- otherwise it was a
        // scroll and the deck stays put.
        if (Math.abs(dx) >= SWIPE_PX && Math.abs(dx) > Math.abs(dy)) {
            swiped.current = true;
            if (dx < 0) onSwipeLeft(); else onSwipeRight();
        }
    };
    const onClick = () => {
        if (swiped.current) { swiped.current = false; return; }
        onTap();
    };
    return (
        <div onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} onClick={onClick}>
            {children}
        </div>
    );
}

// Icons copied verbatim from the set-text reader so the extracted view renders
// byte-identically (the shared icons.jsx variants take different props).
function IconCards({ large }) {
    return (<svg className={large ? 'w-16 h-16' : 'w-3.5 h-3.5'} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="6" width="14" height="12" rx="1.5" /><path d="M7 3h12a1.5 1.5 0 0 1 1.5 1.5V16" /></svg>);
}
function IconChevronLeft() {
    return (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>);
}
function IconChevronRight() {
    return (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>);
}
function IconRefresh() {
    return (<svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-2.64-6.36M21 3v5h-5" /></svg>);
}
